import type { ProductDescriptionSource } from '../types/utils'
/** Application formatters (Angular pipe parity). */

import type { ApplicationFilters } from '../types/application'

export const APPLICATION_FILTER_SESSION_META_KEYS = [
  'company_name',
  'property_name',
  'product_name',
] as const


/** Angular product detail drawers use `product_long_description`; keep `product_description` as API fallback. */
export function getProductLongDescription(application: ProductDescriptionSource | null | undefined): string {
  const longDescription = application?.product_long_description
  if (longDescription != null) return String(longDescription)

  const description = application?.product_description
  return description != null ? String(description) : ''
}

/** Removes UI/session-only keys before GET `/api/application` (Angular parity). */
export function stripApplicationFiltersMeta(filters: ApplicationFilters): ApplicationFilters {
  const next = { ...filters }
  for (const k of APPLICATION_FILTER_SESSION_META_KEYS) {
    delete (next as Record<string, unknown>)[k]
  }
  return next
}

/** Angular `max-rent-list`: `pmc_itr === 1 ? 'PMC' : 'Global'`. */
export function formatMaxRentHistoryItrType(pmcItr: unknown): string {
  return Number(pmcItr) === 1 ? 'PMC' : 'Global'
}

/** Scalar cells for max rent history table (null/empty → em dash). */
export function formatMaxRentHistoryCell(value: unknown): string {
  if (value == null || value === '') return '—'
  return String(value)
}

/** 8-digit applicant id with leading zeros. */
export function formatApplicantId(applicantId: number): string {
  if (!applicantId) return ''
  return String(applicantId).padStart(8, '0')
}

/** 6-digit application id with leading zeros. */
export function formatApplicationId(applicationId: number): string {
  if (!applicationId) return ''
  return String(applicationId).padStart(6, '0')
}

/** Phone display (XXX) XXX-XXXX. */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return ''

  // Remove all non-numeric characters
  const digits = String(phone).replace(/\D/g, '')

  if (digits.length === 10) {
    // Format as (XXX) XXX-XXXX
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  } else if (digits.length === 11) {
    // Format as +X (XXX) XXX-XXXX
    return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }

  return phone
}

/** Normalize API date to YYYY-MM-DD (Angular moment format); ISO strings take date part only. */
export function normalizeDateFromAPI(dateStr: string | null | undefined): string {
  if (!dateStr || typeof dateStr !== 'string') return ''
  const s = dateStr.trim()
  if (!s) return ''
  // ISO with T → take date part only (avoids timezone shift)
  if (s.includes('T')) return s.split('T')[0]
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  // Fallback: parse and format as YYYY-MM-DD using local date parts
  const d = new Date(s)
  if (isNaN(d.getTime())) return ''
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Display date DD/MM/YYYY. */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return ''

  if (typeof date === 'string') {
    const ymd = normalizeDateFromAPI(date)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-')
      return `${day}/${month}/${year}`
    }
    return ''
  }

  const dateObj = date
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return ''
  const day = String(dateObj.getDate()).padStart(2, '0')
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const year = dateObj.getFullYear()
  return `${day}/${month}/${year}`
}

/** Display date as MM/dd/yyyy. */
export function formatDateMMDDYY(date: string | Date | null | undefined): string {
  if (!date) return ''

  if (typeof date === 'string') {
    const ymd = normalizeDateFromAPI(date)
    if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      const [year, month, day] = ymd.split('-')
      return `${month}/${day}/${year}`
    }
    return ''
  }

  const dateObj = date
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return ''
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  const year = String(dateObj.getFullYear())
  return `${month}/${day}/${year}`
}

/** @deprecated Use formatDateMMDDYY — kept for existing imports. */
export const formatPaymentDueDate = formatDateMMDDYY

/** Contract Paid column — API 0|1; matches Angular *ngIf="application?.certificate_paid". */
export function isCertificatePaid(value: unknown): boolean {
  return value === 1 || value === true || value === '1'
}

/** Display date/time MM/dd/yyyy hh:mm:ss a. */
export function formatDateTime(date: string | Date | number | null | undefined): string {
  if (!date) return ''

  let dateObj: Date
  if (typeof date === 'number') {
    // Check if Unix timestamp in seconds (10 digits) or milliseconds (13 digits)
    dateObj = date.toString().length === 10 ? new Date(date * 1000) : new Date(date)
  } else if (typeof date === 'string') {
    const trimmed = date.trim()
    // API often returns unix sec/ms as a numeric string (e.g. mail_trace created_at).
    if (/^\d{10,13}$/.test(trimmed)) {
      const n = Number(trimmed)
      dateObj = trimmed.length === 10 ? new Date(n * 1000) : new Date(n)
    } else {
      // ISO or "YYYY-MM-DD HH:mm:ss"
      dateObj = trimmed.includes(' ')
        ? new Date(trimmed.replace(' ', 'T'))
        : new Date(trimmed)
    }
  } else {
    dateObj = date
  }

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return ''

  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  const year = dateObj.getFullYear()

  let hours = dateObj.getHours()
  const minutes = String(dateObj.getMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getSeconds()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12

  return `${month}/${day}/${year} ${String(hours).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`
}

/** Payment log date — Angular `date:'yyyy-MM-dd hh:mm:ss a'`. */
export function formatPaymentLogDateTime(
  date: string | Date | number | null | undefined,
): string {
  if (!date) return ''

  let dateObj: Date
  if (typeof date === 'number') {
    dateObj = date.toString().length === 10 ? new Date(date * 1000) : new Date(date)
  } else if (typeof date === 'string') {
    dateObj = date.includes(' ') ? new Date(date.replace(' ', 'T')) : new Date(date)
  } else {
    dateObj = date
  }

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return ''

  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  let hours = dateObj.getHours()
  const minutes = String(dateObj.getMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getSeconds()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12

  return `${year}-${month}-${day} ${String(hours).padStart(2, '0')}:${minutes}:${seconds} ${ampm}`
}

/** Date-only for credit summary (MM/dd/yyyy); matches Angular-style credit headers. */
export function formatCreditReportLineDate(value: unknown): string {
  if (value == null || value === "") return "—"
  const s = String(value).trim()
  if (!s) return "—"
  const full = formatDateTime(s)
  if (full) {
    const datePart = full.split(" ")[0]
    return datePart && datePart.length > 0 ? datePart : full
  }
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return s
  return "—"
}

/** Hide @noemail.com placeholder emails (Angular noemail pipe). */
export function formatEmail(email: string): string {
  if (!email) return ''
  // Angular noemail pipe: hide placeholder emails containing '@noemail.com'
  if (email.includes('@noemail.com')) return ''
  return email
}

/** MM-DD-YYYY → DD-MM-YYYY for API. */
export function formatDateForAPI(dateStr: string): string {
  if (!dateStr) return ''
  // Parse MM-DD-YYYY and convert to DD-MM-YYYY
  const parts = dateStr.split('-')
  if (parts.length === 3) {
    return `${parts[1]}-${parts[0]}-${parts[2]}` // DD-MM-YYYY
  }
  return dateStr
}

/** DD-MM-YYYY → MM-DD-YYYY for display. */
export function formatDateFromAPI(dateStr: string): string {
  if (!dateStr) return ''
  // Parse DD-MM-YYYY and convert to MM-DD-YYYY
  const parts = dateStr.split('-')
  if (parts.length === 3) {
    return `${parts[1]}-${parts[0]}-${parts[2]}` // MM-DD-YYYY
  }
  return dateStr
}


